// AuthRoute.tsx
import React, { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router-dom";
import { getOptionalEnvVar, checkRequiredEnvVars } from "./utils/environment";

interface AuthRouteProps {
  children: React.ReactNode;
}

const AuthRoute: React.FC<AuthRouteProps> = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();

  // If not authenticated, save current path and redirect to Cognito login
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      try {
        // Store the full current path including any query parameters
        const currentPath = window.location.pathname + window.location.search;
        localStorage.setItem('originalPath', currentPath);
        console.log("Storing original path before redirect:", currentPath);
        
        // Check if we have user tokens already in localStorage
        const hasTokens = localStorage.getItem('id_token') && 
                          localStorage.getItem('access_token');
                          
        if (hasTokens) {
          console.log("Found existing tokens, attempting to use them");
          // The tokens exist but might be expired - we'll let the auth library handle that
        }
        
        // Verify required environment variables for authentication
        const { isAvailable, missingVars } = checkRequiredEnvVars([
          'REACT_APP_COGNITO_DOMAIN',
          'REACT_APP_CLIENT_ID'
        ]);
        
        if (!isAvailable) {
          console.error(`Cannot redirect to login: Missing required environment variables: ${missingVars.join(', ')}`);
          return;
        }
        
        // Redirect to Cognito login with error handling
        auth.signinRedirect().catch(error => {
          console.error("Error redirecting to login:", error);
          // If there's an error with the redirect, try a direct approach
          const cognitoDomain = getOptionalEnvVar('REACT_APP_COGNITO_DOMAIN', '');
          const clientId = getOptionalEnvVar('REACT_APP_CLIENT_ID', '');
          
          if (cognitoDomain && clientId) {
            const redirectUri = encodeURIComponent(window.location.origin);
            const loginUrl = `https://${cognitoDomain}/oauth2/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}`;
            console.log("Falling back to direct login URL:", loginUrl);
            window.location.href = loginUrl;
          } else {
            console.error("Cannot redirect to login: Missing environment variables");
          }
        });
      } catch (error) {
        console.error("Authentication error:", error);
      }
    }
  }, [auth.isAuthenticated, auth.isLoading, auth, location]);

  // Show loading state while checking authentication
  if (auth.isLoading) {
    return <div>Verifying authentication...</div>;
  }

  // Only render children when authenticated
  return auth.isAuthenticated ? <>{children}</> : <div>Redirecting to login...</div>;
};

export default AuthRoute;
